import { Location } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IHeartbeatResult } from '@interfaces/heartbeat-result';
import { IUserInformation } from '@interfaces/userInformation';
import * as UserInfoActions from '@state/actions/user-profile.actions';
import { selectUserInformation } from '@state/selectors/user-profile.selectors';
import { TapAppStore } from '@state/tap-state';
import {
  NullValidationHandler,
  OAuthErrorEvent,
  OAuthService,
} from 'angular-oauth2-oidc';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CacheBuster, globalCacheBusterNotifier } from 'ts-cacheable';
import { ActiveFilter } from '../modules/tank-quick-filters/components/global-tank-filters/tank-quick-filters.component';
import { AppSettings } from '../shared/Constants/sharedConstants';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { api } from '../shared/communication-objects/api-path';
import { AppDialogService } from './app-dialog.service';
import { EXPIRE_SESSION_ON_401 } from './authinterceptor.service';
import { AUTH_CONFIG, SecurityPolicies } from './auth-variables';
import { LogService } from './log.service';
import { OrganizationFeaturesService } from './organization-features.service';
import { OrganizationService } from './organization.service';
import { SignalRService } from './signalr.service';
import { TabOrganizationService } from './tab-organization.service';
import { TankFilterService } from './tank-filter.service';
import { TokenRefreshService } from './token-refresh.service';
import { TokenService } from './token.service';
import { LOGIN_REASON_PARAM, LoginReason, returnUrlQueryParams } from '../utilities/return-url';

(window as any).global = window;

/** The session expireSession tore down, kept for the login page that follows - see resumeExpiredSession. */
const EXPIRED_SESSION_KEY = 'tl.tab.expiredSession';

interface ExpiredSession {
  userId: number;
  organizationId: number | null;
}

export interface ILoginPageModel {
  themePathPrefix: string;
  font: string;
  colors: string;
  desktopLogo: string;
  logoBackgroundColor: string;
  mobileLogo: string;
  logoHyperlink: string;
  logToSiteUsageTracker: boolean;
  siteTrackerUrl: string;
  siteTrackerSiteId: string;
  appleStoreLink: string;
  googleStoreLink: string;
  loginData: ILoginData;
  /** The password was right, but the address must be confirmed with an emailed code first. */
  emailVerification?: IEmailVerificationChallenge;
  /** The password was right, and a second factor must follow. */
  secondFactor?: ISecondFactorChallenge;
}

/**
 * The second step a correct password led to - TL.Portal.API.DTO.SecondFactorChallengeDTO, camelCased
 * (openspec add-second-factor-sign-in). The challenge is held in memory only.
 */
export interface ISecondFactorChallenge {
  /** Null when limitReached. */
  challenge: string | null;
  /** The user holds no factor yet: they set one up, and its first code signs them in. */
  enrolment: boolean;
  factors: ISecondFactorOption[];
  /** The one last used, offered first. */
  defaultFactor?: SecondFactorType | null;
  /** A user who is not required may ask for an emailed code instead. */
  emailFallback: boolean;
  /** Days this browser is trusted for afterwards; 0 for a super user. */
  trustDays: number;
  /** The account's mobile number, to prefill enrolment. */
  suggestedPhoneNumber?: string | null;
  limitReached: boolean;
  /** UTC. */
  retryAfter?: string | null;
}

export type SecondFactorType = 'sms' | 'totp';
export type SecondFactorChannel = 'sms' | 'call';

export interface ISecondFactorOption {
  type: SecondFactorType;
  /** The number's last digits, masked. Null for an authenticator app. */
  hint?: string | null;
}

/** Shown once, while an authenticator app is being set up. */
export interface ITotpEnrolment {
  otpauthUri: string;
  manualKey: string;
}

/**
 * The email-verification step a correct password led to - TL.Portal.API.DTO.EmailVerificationChallengeDTO,
 * camelCased (openspec require-verified-email-at-signin). The challenge is held in memory only.
 */
export interface IEmailVerificationChallenge {
  /** Null when limitReached. */
  challenge: string | null;
  /** Where the code went, masked. Null when addressRequired, or when the page was opened from the link. */
  addressHint: string | null;
  addressRequired: boolean;
  /** A user-wide limit holds (`limit` says which, `retryAfter` when it lifts): there is no challenge. */
  limitReached: boolean;
  limit?: 'emails' | 'wrong-codes' | null;
  /** UTC. */
  retryAfter?: string | null;
  /** A verified user on a browser that is not trusted gets `new-device`. Absent when limitReached. */
  kind?: EmailVerificationKind;
  /** Days this browser is trusted for once a new-device check completes; 0 for a super user. */
  trustDays?: number;
}

export type EmailVerificationKind = 'email-verification' | 'new-device';

export interface ILoginData {
  username: string;
  password: string;
  rememberMe: boolean;
  error: string;
}

export const RoleNames = {
  SuperUser: 'SuperUser',
  PrimaryContact: 'PrimaryContact',
};

const FeatureNames = {
  Theming: 'Theming',
  AnomalyDetection: 'Anomaly Detection',
};

const PermissionNames = {
  HasWebAccess: 'HasWebAccess',
  ManageThemes: 'ManageThemes',
  ManageOrganizations: 'ManageOrganizations',
  ManagePremiumServices: 'ManagePremiumServices',
  ManageAlarms: 'ManageAlarms',
  ManageTanks: 'ManageTanks',
  ManageCallSchedule: 'ManageCallSchedule',
  ManageUsers: 'ManageUsers',
  ManageLocations: 'ManageLocations',
  ManageProducts: 'ManageProducts',
  ManageTankProfile: 'ManageTvp',
  ManageDiagnostic: 'ManageDiagnostic',
};

export interface IResetPasswordResponse {
  success: boolean;
  errorMessages: string[];
}

/** Shape of the Authenticate/token response - TL.Portal.API.DTO.LoginResult, camelCased. */
interface IAuthenticateTokenResult {
  success: boolean;
  error: string;
  token?: {
    body: string;
    expiryInUtc: string;
  };
  emailVerification?: IEmailVerificationChallenge;
  mfa?: ISecondFactorChallenge;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public securityPolicies: any = SecurityPolicies;
  private _userBehaviorSubject: BehaviorSubject<IUserInformation>;
  private userId: number;
  private debugImpersonatingId: any;
  public loggedIn$: Subject<boolean> = new Subject<boolean>();
  private sessionExpiring = false;
  /** Set when this tab chose Sign out, until it signs in again. */
  private signedOutHere = false;
  private organizationChangesSubscribed = false;
  private announcedOrganizationId: number | null = null;

  constructor(
    public router: Router,
    private oauthService: OAuthService,
    private restClient: DSRESTClient,
    private cookieService: CookieService,
    private appStore: TapAppStore,
    private logger: LogService,
    private location: Location,
    private ofService: OrganizationFeaturesService,
    private organizationService: OrganizationService,
    private appDialogService: AppDialogService,
    private tankFilterService: TankFilterService,
    private signalRService: SignalRService,
    private tokenService: TokenService,
    private tokenRefreshService: TokenRefreshService,
    private translate: TranslateService,
    private tabOrganization: TabOrganizationService,
  ) {}

  public subscribeToLogoutNotifications() {
    this.logger.log('Subscribing to logout notifications via SignalR...');
    this.signalRService
      .getLogoutNotifications()
      .pipe(
        filter((n) => n?.data?.action === 'logout'),
        take(1),
      )
      .subscribe((notification) => {
        this.logger.log(
          'Received logout notification via SignalR:',
          notification,
        );

        // Our own sign-out, coming back: logout() has already done everything, and treating it as
        // a sign-out elsewhere would leave a stray expired-session record behind for the next sign-in.
        if (this.signedOutHere) return;

        // The sign-out happened somewhere else, so this tab's page is work nobody abandoned - keep it
        // the way an expiry does, behind the same same-user check and organization restore.
        this.expireSession('signed-out-elsewhere');
      });
  }

  /**
   * The counterpart to subscribeToLogoutNotifications, for the selected organization.
   *
   * The organization is one row shared by every tab, window and device this user has open - and by
   * the legacy app - while the id that scopes their queries is a claim baked into each session's
   * own token. Nothing about a switch made elsewhere reaches this tab on its own; it carries on
   * querying the organization it was opened in, while several backend services read the shared row
   * instead of the claim and answer for the new one. The two disagree with no error anywhere.
   *
   * The server excludes the connection that asked for the change, so this never fires in the tab
   * that made it. Not `take(1)` like the logout subscription: the organization can move more than
   * once in a session, and each move needs handling.
   */
  public subscribeToOrganizationChangeNotifications() {
    // Unlike the logout subscription this one is not bounded by take(1) - the organization can move
    // more than once in a session - so it has to be guarded against stacking. AuthService is root
    // provided and outlives AuthShellComponent, which calls this from ngAfterViewInit; a shell
    // rebuilt without a page reload would otherwise add a second subscription, and the user would
    // be told twice and refetch their profile twice for one change.
    if (this.organizationChangesSubscribed) return;
    this.organizationChangesSubscribed = true;

    this.logger.log('Subscribing to organization change notifications via SignalR...');
    this.signalRService
      .getOrganizationChangeNotifications()
      .subscribe((notification) => {
        const organizationId = notification?.data?.organizationId;
        if (!organizationId) return;

        this.applyExternalOrganizationChange(
          organizationId,
          notification?.data?.organizationName,
        );
      });
  }

  /**
   * Handles an organization change made somewhere else, per AppSettings.csoChangeBehaviour.
   *
   * Under `silent` nothing happens at all: the tabs are simply independent. Under `notify` the tab
   * is likewise left as it is - same credential, same claim, same data - and the user is told why
   * another tab disagrees with this one. Both are coherent rather than a compromise: the CSO claim
   * on this session's own token is the only thing any scoping or authorization decision reads, so a
   * tab on an older claim is internally consistent, it is simply looking at a different organization
   * than another tab is.
   *
   * Under `converge` the tab follows: a credential carrying the new organization, a re-read profile
   * and busted caches, and then the same message - because what is on screen was still painted for
   * the organization they have just left.
   *
   * Reloading is the user's call either way. Doing it for them destroys unsaved work they cannot
   * get back, and there is no way from here to know whether a form is half-filled.
   */
  public applyExternalOrganizationChange(
    organizationId: number,
    organizationName: string,
  ): void {
    // Tabs are independent and nobody is told. Checked before anything else, including the
    // already-announced bookkeeping, so that flipping the knob to `notify` at runtime announces the
    // next report rather than suppressing it as already seen.
    if (AppSettings.csoChangeBehaviour === 'silent') return;

    const currentOrganizationId = this._userBehaviorSubject?.value
      ?.lastSelectedOrganizationId;

    // Already there. Two tabs can sit on the same organization, and a tab that refreshed for its own
    // reasons has picked the change up already.
    if (organizationId && organizationId === currentOrganizationId) return;

    // Under `notify` nothing here resolves the divergence, so the heartbeat keeps finding it and
    // reporting it on every beat. Without this the user is told again every few minutes, forever,
    // about one change they have already seen. Keyed on the organization so a SECOND move, or a move
    // back, still gets announced.
    if (organizationId && organizationId === this.announcedOrganizationId) return;
    this.announcedOrganizationId = organizationId;

    if (AppSettings.csoChangeBehaviour === 'notify') {
      this.announceOrganizationChange(organizationId, organizationName);
      return;
    }

    // Before the refresh, not after: the refresh sends this tab's intent, so leaving it unset would
    // have the server hand back a credential for the organization this tab was ALREADY in and
    // converge would quietly stop converging.
    this.tabOrganization.set(organizationId);

    this.tokenRefreshService
      .refresh()
      .pipe(
        take(1),
        switchMap(() => {
          globalCacheBusterNotifier.next();
          return this.getUserInformation(true);
        }),
        take(1),
      )
      .subscribe({
        next: (u: IUserInformation) => {
          this.appStore.store.dispatch(new UserInfoActions.GetUserInfo(u));
          this.announceOrganizationChange(
            organizationId,
            organizationName || u?.lastSelectedOrganizationName,
          );
        },
        error: () => {
          // The credential could not be rebuilt or the profile could not be read, so this tab did
          // not converge after all. Say so anyway and offer the reload, which bootstraps a fresh
          // token through the app initializer.
          this.announceOrganizationChange(organizationId, organizationName);
        },
      });
  }

  private announceOrganizationChange(
    organizationId: number,
    organizationName: string,
  ): void {
    // Says what actually happened. This session has already moved - token and profile both - so
    // claiming the page "is showing the previous organization" contradicted the organization name
    // in the header, which updates from the same dispatch above. What is genuinely stale is the
    // data already painted, and reloading is what fixes that.
    //
    // Both keys live in the CSO string-resource group; see
    // TL.DatabaseScripts/DML/SBTMTAPWP-2176/cso-changed-elsewhere-string-resources.sql. That
    // script has to be applied wherever this code runs or the snackbar shows the raw key.
    const message = organizationName
      ? this.translate.instant('CSO.OrganizationChangedElsewhere', {
          organization: organizationName,
        })
      : this.translate.instant('CSO.OrganizationChangedElsewhereUnknown');

    this.appDialogService
      .showActionMessage(message, this.translate.instant('CSO.ReloadPage'))
      .onAction()
      .pipe(take(1))
      .subscribe(() => {
        // Pressing Reload IS the user accepting the change, so it is an intent change and has to be
        // recorded before the page goes. Without this the reload sends the organization this tab was
        // already in and lands them exactly where they started - the button would look broken.
        this.tabOrganization.set(organizationId);
        window.location.reload();
      });
  }

  /**
   * The other half of the guardrail comment on login() above: this already targets a real tenant
   * and App Registration (AUTH_CONFIG in auth-variables.ts, environment.ts's sso/userdetail) via a
   * proper Authorization Code + PKCE flow, but nothing calls this method - it isn't wired into app
   * bootstrap. This, not Authenticate/token, is where an Entra ID migration should resume from.
   */
  public configureWithNewConfigApi() {
    this.oauthService.configure(AUTH_CONFIG);
    this.oauthService.setStorage(localStorage);
    this.oauthService.tokenValidationHandler = new NullValidationHandler();
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.events.subscribe((event) => {
      if (event instanceof OAuthErrorEvent) {
        console.error(event);
      } else {
        console.warn(event);
      }
    });
    return this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .then((loggedIn) => {
        if (loggedIn) {
          this.loadUserProfile().subscribe(this.processUserLoaded.bind(this));
        }
      });
  }

  @CacheBuster({
    isInstant: true,
  })
  public updateLastSelectedOrganization(
    orgId: number,
    overrideFilter: boolean = false
  ): Observable<IUserInformation> {
    globalCacheBusterNotifier.next();
    return this.restClient.get(api`UserProfile/UpdateLastSelectedOrganization/${orgId}`).pipe(
      switchMap(() => this.getUserInformation(true)),
      take(1),
      tap({
        next: (u: IUserInformation) => {
          this.logger.log('updateLastSelectedOrganization next: ', u);
          this.appStore.store.dispatch(new UserInfoActions.GetUserInfo(u));

          // This tab's own choice, so it becomes this tab's intent - which is what keeps it here
          // after a reload instead of adopting whatever another tab picked later.
          this.tabOrganization.set(u?.lastSelectedOrganizationId);

          if (overrideFilter)
            return;

          this.tankFilterService
            .setLastSelectedTankQuickFilter(
              ActiveFilter,
              null,
              this.constructor.name,
            )
            .then(() => {});
        },

        error: () => {
          this.appDialogService.showErrorMessage(
            'An error occurred while updating your organization.',
          );
        },
      }),
    );
  }

  public canSelectOrganizationForCso(orgId: number): Observable<boolean> {
    return this.restClient.getDirect<boolean>(api`UserProfile/CanSelectOrganizationForCso/${orgId}`);
  }

  public updateLastSelectedTankProfile(tankProfileId: number, orgId: number) {
    return this.restClient.get(api`UserProfile/UpdateLastSelectedTankProfile/${tankProfileId}/${orgId}`);
  }

  public setUserId(id: number) {
    this.userId = id;
    this.cookieService.delete('USERID'); //delete this cookie, it's not used anymore
  }
  public getUserId(): string {
    return this.userId.toString();
  }

  public getUserInformation(
    refreshCacheFromServer: boolean = false,
  ): Observable<IUserInformation> {
    this.logger.log(
      'getUserInformation refresh Cache From Server: ' + refreshCacheFromServer,
    );
    //get the user's information and store locally for security lookup
    //return's an observable of the internal user store.
    let userInformation: Observable<IUserInformation>;

    if (refreshCacheFromServer) {
      userInformation = this.getUserFromServer();
    } else {
      userInformation = this.loadUserProfile();
    }

    return userInformation.pipe(
      switchMap((userInfo) => {
        this.processUserLoaded(userInfo);
        return this._userBehaviorSubject;
      }),
    );
  }

  public getOriginatingUser(): number {
    return this._userBehaviorSubject?.getValue()?.originatingUserId ?? 0;
  }

  public getImpersonatingUserId(): number {
    if (this.debugImpersonatingId) {
      return Number(this.debugImpersonatingId);
    }
    return this._userBehaviorSubject?.getValue()?.impersonatingUserId ?? 0;
  }

  public getIsImpersonating(): boolean {
    return this._userBehaviorSubject?.getValue()?.isImpersonating ?? false;
  }

  /**
   * Password-grant (ROPC) login against Authenticate/token - functional, but a deliberately
   * interim foundation. It's the same endpoint the mobile tank-installer app already used, not a
   * new one built for this migration, and it isn't the shape to build an Entra ID login on:
   * Entra never sees an interactive sign-in here, so MFA/Conditional Access/passwordless can't
   * apply. A real Entra ID login is a redirect-based Authorization Code + PKCE flow instead - see
   * configureWithNewConfigApi() below and AUTH_CONFIG in auth-variables.ts, which already points
   * at a real tenant and App Registration (environment.ts) and just isn't wired up to anything
   * yet. That's where a future migration should pick up, not here.
   */
  public login(
    userName: string,
    password: string,
  ): Observable<ILoginPageModel> {
    // Body, never the query string: a URL ends up in proxy logs, access logs and request telemetry.
    this.sessionExpiring = false;
    this.signedOutHere = false;
    return this.restClient.postForResponse<IAuthenticateTokenResult>(api`Authenticate/token`, { userName, password }).pipe(
      tap((result) => {
        if (result?.success && result.token?.body) {
          this.tokenService.setToken(result.token.body, result.token.expiryInUtc);
        }
      }),
      map((result) => this.toLoginPageModel(userName, result)),
    );
  }

  /** Emails a new code on the challenge: 202, or 410 once it is over, or 429 at a limit. */
  public sendEmailVerificationCode(challenge: string): Observable<unknown> {
    return this.restClient.postForResponse(api`Authenticate/email-verification/send`, { challenge });
  }

  /** For a user with no usable address on file: where to send the code. Saved only once the code is accepted. */
  public setEmailVerificationAddress(challenge: string, emailAddress: string): Observable<unknown> {
    return this.restClient.postForResponse(api`Authenticate/email-verification/address`, {
      challenge,
      emailAddress,
    });
  }

  /**
   * A correct code signs in exactly as a correct password does: the token, and the refresh cookie. A user
   * who must also use a second factor gets that challenge instead, with no session yet.
   */
  public verifyEmailVerificationCode(challenge: string, code: string): Observable<boolean | ISecondFactorChallenge> {
    return this.restClient
      .postForResponse<IAuthenticateTokenResult>(api`Authenticate/email-verification/verify`, { challenge, code })
      .pipe(map((result) => result?.mfa ?? this.tookToken(result)));
  }

  /** Sends a code to the challenge's phone number: 202, or 410 once it is over, 429 at a limit, 503 if it cannot. */
  public sendSecondFactorCode(challenge: string, channel: SecondFactorChannel): Observable<unknown> {
    return this.restClient.postForResponse(api`Authenticate/mfa/sms/send`, { challenge, channel });
  }

  /** A correct code signs in: the token, and the refresh cookie. */
  public verifySecondFactorCode(challenge: string, type: SecondFactorType, code: string): Observable<boolean> {
    return this.restClient
      .postForResponse<IAuthenticateTokenResult>(api`Authenticate/mfa/verify`, { challenge, type, code })
      .pipe(map((result) => this.tookToken(result)));
  }

  /** Holds the number on the enrolment, and texts it a code. A number we will not send to is a 400. */
  public enrolSecondFactorPhone(challenge: string, phoneNumber: string): Observable<unknown> {
    return this.restClient.postForResponse(api`Authenticate/mfa/enrol/sms`, { challenge, phoneNumber });
  }

  /** A new authenticator secret, in this one response only. */
  public enrolSecondFactorTotp(challenge: string): Observable<ITotpEnrolment> {
    return this.restClient.postForResponse<ITotpEnrolment>(api`Authenticate/mfa/enrol/totp`, { challenge });
  }

  /** The new factor's first code, which also signs the user in. */
  public confirmSecondFactor(challenge: string, type: SecondFactorType, code: string): Observable<boolean> {
    return this.restClient
      .postForResponse<IAuthenticateTokenResult>(api`Authenticate/mfa/enrol/confirm`, { challenge, type, code })
      .pipe(map((result) => this.tookToken(result)));
  }

  /** "Email me a code instead", for a user who is not required: the emailed-code step that replaces this one. */
  public secondFactorEmailFallback(challenge: string): Observable<IEmailVerificationChallenge> {
    return this.restClient.postForResponse<IEmailVerificationChallenge>(api`Authenticate/mfa/email-fallback`, { challenge });
  }

  private tookToken(result: IAuthenticateTokenResult): boolean {
    if (!result?.success || !result.token?.body) return false;
    this.tokenService.setToken(result.token.body, result.token.expiryInUtc);
    return true;
  }

  /**
   * Whether this browser already holds a session from the refresh cookie: the email link finished the
   * sign-in in another tab of the same browser.
   */
  public resumeFromRefreshCookie(): Observable<boolean> {
    return this.tokenRefreshService.refresh().pipe(take(1));
  }

  /**
   * Adapts the Authenticate/token response (LoginResult) into the ILoginPageModel shape
   * login.component.ts already expects, so the login-cookie-era UI needed no changes here. The
   * theme fields were already server-defaults-only under the old cookie login (see
   * LoginController.SetupReplyModel) and stay that way.
   */
  private toLoginPageModel(userName: string, result: IAuthenticateTokenResult): ILoginPageModel {
    return {
      themePathPrefix: '',
      font: '',
      colors: '',
      desktopLogo: '',
      logoBackgroundColor: '',
      mobileLogo: '',
      logoHyperlink: '',
      logToSiteUsageTracker: false,
      siteTrackerUrl: '',
      siteTrackerSiteId: '',
      appleStoreLink: '',
      googleStoreLink: '',
      loginData: {
        username: userName,
        password: '',
        rememberMe: false,
        error:
          result?.success || result?.emailVerification || result?.mfa
            ? ''
            : (result?.error ?? 'username or password is invalid'),
      },
      emailVerification: result?.emailVerification,
      secondFactor: result?.mfa,
    };
  }

  public logout(options?: any): void {
    // This tab is still in the user's SignalR group, so the broadcast its own logout triggers comes
    // straight back to it - see subscribeToLogoutNotifications.
    this.signedOutHere = true;
    this.processLogout(options);
    this.navigateToLogin();
  }

  private navigateToLogin() {
    this.router.navigate(['login']);
  }

  private processLogout(_options): void {
    // Fire the server-side logout - and the SignalR broadcast to the user's other sessions it
    // triggers - before clearing the token below. Once cleared, this request would go out with no
    // Authorization header, and UserSecurity.UserId() would have nothing to broadcast a logout
    // for. This didn't matter under cookies, since sessionStorage.clear() never touched the cookie.
    const logoutUrl = api`logout`;
    this.restClient.get(logoutUrl).pipe(take(1)).subscribe();

    // Separate call, because the refresh cookie is path-scoped to Authenticate/refresh and the
    // browser will not apply a cross-origin deletion sent from anywhere else - see
    // TokenRefreshService.revoke. Without it a logout under ng serve leaves the credential in place
    // and the next reload bootstraps a new token from it.
    this.tokenRefreshService.revoke().pipe(take(1)).subscribe();

    // Before the token is cleared below: once it is gone every reconnect can only be refused, and the
    // hub would retry that for as long as the page stays open. The next sign-in reopens it.
    this.signalRService.stop();

    // Forget which organization this tab was in. sessionStorage outlives a logout - it is cleared
    // only when the tab closes - so without this the next person to sign in here would be put into
    // the previous user's organization on their first reload, and the server would honour it if they
    // happen to have access.
    this.tabOrganization.clear();

    // Open dialogs live in the CDK overlay container on <body>, so navigating to login does not
    // touch them - they stay pinned over the login page, and the ones opened with `disableClose`
    // (the bulk tank config wizard, the settings panel) cannot be dismissed by hand either. This
    // runs for every way a session ends: the logout button, an expired session, and the SignalR
    // logout broadcast all come through here.
    this.appDialogService.dismissAllOverlays();

    //otherwise, if the authenticated user is the one logging out,
    //clean up their session and log out
    this.oauthService.logOut();
    localStorage.clear();
    sessionStorage.clear();
    this.tokenService.clearToken();
    this.appStore.logout();
    // The theme is a property of the URL the visitor arrived on, not of who they are, so it
    // deliberately survives logout. The remembered slug lives in an HttpOnly cookie, which
    // deleteAll cannot see, so it survives this too. Pushing null at themeConfig$ here only ever
    // looked like a reset - the service filters nulls out.
    this.cookieService.deleteAll();
  }

  /**
   * Ends a session this tab did not choose to end, keeping its page to return to.
   *
   * Called by AuthInterceptor when a request that opted into EXPIRE_SESSION_ON_401 is rejected with
   * 401 - in practice the heartbeat. The auth cookie is a sliding cookie with an absolute cap, so
   * a 401 there means the session is dead server-side and cannot be refreshed - clear it down and
   * send the user to login with a reason, rather than leaving a dead page on screen until the next
   * full reload. A 401 on an ordinary API call is an authorization failure and does not come here.
   *
   * Also called with `signed-out-elsewhere` when the UserLogout broadcast reaches a tab that did not
   * sign out itself - see subscribeToLogoutNotifications.
   */
  public expireSession(reason: LoginReason = 'expired'): void {
    if (this.sessionExpiring) return;
    this.sessionExpiring = true;

    // Signing back in returns them to where the session died. When LoggedInGuard's heartbeat is what
    // 401'd, the navigation it was guarding has not committed and router.url is still the previous
    // page ('/' on a fresh deep link), so the in-flight target is the page they actually asked for.
    // A deliberate logout carries no returnUrl: the next person to sign in on this machine is not them.
    const pending = this.router.getCurrentNavigation()?.extractedUrl;
    const returnTo = returnUrlQueryParams(
      pending ? this.router.serializeUrl(pending) : this.router.url,
    );

    // Read before processLogout clears it. The returnUrl only makes sense in the organization this
    // tab was in, and signing back in scopes the new token to the shared column instead - see
    // resumeExpiredSession.
    const user = this._userBehaviorSubject?.getValue();
    const expired: ExpiredSession | null =
      user?.userId > 0
        ? {
            userId: user.userId,
            organizationId: this.tabOrganization.get() ?? user.lastSelectedOrganizationId,
          }
        : null;

    this.processLogout({ forceLogout: true });

    // After processLogout, which clears sessionStorage. Tab-scoped like the organization it carries.
    if (expired) {
      try {
        sessionStorage.setItem(EXPIRED_SESSION_KEY, JSON.stringify(expired));
      } catch {
        // Privacy modes can refuse sessionStorage; the login page then treats the returnUrl as an
        // ordinary deep link.
      }
    }

    this.router.navigate(['login'], { queryParams: { [LOGIN_REASON_PARAM]: reason, ...returnTo } });
  }

  /**
   * Called by the login page once the new session's profile has loaded. Puts this tab back in the
   * organization its expired session was in and answers whether the returnUrl may be followed.
   *
   * Authenticate/token builds the new token from the shared User.LastSelectedOrganizationId column,
   * so without this a tab whose session died in one organization signs back in to whichever one
   * another tab or device picked last, and the returnUrl reopens its page there. The restore goes
   * through Authenticate/refresh with this tab's organization, the same validated, never-written-back
   * path a page reload uses, so it does not move the user's other tabs.
   *
   * - No expired session recorded: an ordinary deep link, followed as-is. Entity routes switch the
   *   organization themselves (CsoFollowService).
   * - A different user signed in: refused, so the last user's page cannot move their organization.
   *   That includes signing back in after a session that expired while impersonating.
   * - The organization could not be restored: refused, so the page does not reopen elsewhere.
   */
  public resumeExpiredSession(userInfo: IUserInformation | null): Observable<boolean> {
    const expired = this.takeExpiredSession();
    if (!expired) return of(true);
    if (!userInfo || expired.userId !== userInfo.userId) return of(false);
    if (!expired.organizationId || expired.organizationId === userInfo.lastSelectedOrganizationId) {
      return of(true);
    }

    // Before the refresh, which sends this tab's intent as the organization to scope to.
    this.tabOrganization.set(expired.organizationId);

    // A failed refresh clears the token, and the one just issued by login is still good.
    const token = this.tokenService.getAccessToken();
    const expiry = this.tokenService.getExpiry();

    return this.tokenRefreshService.refresh().pipe(
      take(1),
      switchMap((refreshed) => {
        if (!refreshed) {
          this.tokenService.setToken(token, expiry);
          this.tabOrganization.set(userInfo.lastSelectedOrganizationId);
          return of(false);
        }

        globalCacheBusterNotifier.next();
        return this.getUserInformation(true).pipe(
          take(1),
          map((u) => {
            this.appStore.store.dispatch(new UserInfoActions.GetUserInfo(u));
            // The server falls back to the stored organization when the requested one is out of
            // reach, so record what this tab actually ended up in.
            this.tabOrganization.set(u?.lastSelectedOrganizationId);
            return u?.lastSelectedOrganizationId === expired.organizationId;
          }),
        );
      }),
      catchError(() => of(false)),
    );
  }

  /** Reads and forgets the session expireSession recorded, so it is acted on once at most. */
  private takeExpiredSession(): ExpiredSession | null {
    try {
      const stored = sessionStorage.getItem(EXPIRED_SESSION_KEY);
      sessionStorage.removeItem(EXPIRED_SESSION_KEY);
      return stored ? (JSON.parse(stored) as ExpiredSession) : null;
    } catch {
      return null;
    }
  }

  public GetBuildInfo(): Observable<any> {
    const endPoint = api`UserProfile/GetBuildInfo`;
    return this.restClient.get(endPoint);
  }

  /**
   * Forgotten password: one username or email address. The answer is always the same success,
   * whether or not it matched an account; the link goes only to the matched account's own address.
   */
  public resetUserPassword(identifier: string): Observable<IResetPasswordResponse> {
    const endPoint = api`user/reset-password`;
    return this.restClient.postForResponse(endPoint, { identifier });
  }

  public silentRefresh() {
    return this.oauthService.silentRefresh();
  }

  //Process an Authorization Policy against the UserInformation.
  //return an Observable of boolean (Policy Pass Result).
  public passesPolicyOf(
    securityPolicy: SecurityPolicies | SecurityPolicies[],
    options: any = { data: null },
  ): Observable<boolean> {
    //if the security policy is NOT an array return the result of passesPolicyOf
    if (!Array.isArray(securityPolicy)) {
      return this.processPolicy(securityPolicy, options);
    }

    //loop through the array of security policies, and validate each one. Combine the results and return an observable.
    return this.processPolicyArray(securityPolicy, options);
  }

  private processPolicyArray(
    securityPolicy: SecurityPolicies[],
    options: any,
  ): Observable<boolean> {
    return this.getUserInformation().pipe(
      // Only do this once.
      take(1),

      // Process policy logic
      switchMap(() => {
        return forkJoin(
          securityPolicy.map((sp) => this.processPolicy(sp, options)),
        ).pipe(
          map((results: boolean[]) => results.every((r) => r)), // Use map for boolean transformation
        );
      }),
    );
  }

  private processPolicy(
    securityPolicy: SecurityPolicies,
    options: any,
  ): Observable<boolean> {
    return this.getUserInformation().pipe(
      //only do this once.
      take(1),

      //process policy logic
      switchMap((userInfo) => {
        switch (securityPolicy) {
          // case SecurityPolicies.BlockAutomaticRedirectToNewUI :
          //   return of(
          //     userInfo?.roles.includes(RoleNames.SuperUser) ||
          //       userInfo?.permissions.includes(PermissionNames.ManageUsers),
          //   );
          case SecurityPolicies.HasWebAccess:
            return of(
              userInfo?.permissions?.includes(PermissionNames.HasWebAccess),
            );

          case SecurityPolicies.RequireSuperUser:
            return of(userInfo?.roles?.includes(RoleNames.SuperUser));

          case SecurityPolicies.RequiresPrimaryContact:
            return of(
              userInfo?.roles?.includes(RoleNames.PrimaryContact) ||
                userInfo?.roles?.includes(RoleNames.SuperUser),
            );

          case SecurityPolicies.RequiresManageThemes:
            return of(
              userInfo?.permissions?.includes(PermissionNames.ManageThemes) ||
                userInfo?.roles?.includes(RoleNames.PrimaryContact),
            );
          case SecurityPolicies.RequiresManageOrganizationsOrPremiumServices:
            this.logger.debug(
              'RequiresManageOrganizationsOrPremiumServices ===================================',
              userInfo,
            );
            return of(
              userInfo?.permissions?.includes(
                PermissionNames.ManageOrganizations,
              ) ||
                userInfo?.permissions?.includes(
                  PermissionNames.ManagePremiumServices,
                ),
            );
          case SecurityPolicies.RequiresManageAlarms:
            return of(
              userInfo?.permissions?.includes(PermissionNames.ManageAlarms),
            );
          case SecurityPolicies.RouteOrgHasAnomalyDetection: {
            //get the orgId from the route using ng Location
            const routeOrgId = this.getRouteOrgId();
            return this.ofService
              .getOrganizationFeatures(routeOrgId)
              .pipe(
                switchMap((orgFeatures) =>
                  of(
                    orgFeatures?.features?.some(
                      (f) =>
                        f.name === FeatureNames.AnomalyDetection &&
                        f.value === true,
                    ),
                  ),
                ),
              );
          }
          case SecurityPolicies.RouteOrgIsBillagleOrg: {
            const routeOrgId = this.getRouteOrgId();
            //debugger; // 5
            return this.organizationService.getOrganization(routeOrgId).pipe(
              map((org) => {
                return org?.organizationDetails?.isBillableOrg ?? false;
              }),
            );
          }
          case SecurityPolicies.RequiresThemingAndHasManageOrgs: {
            const hasManageOrgs = userInfo.permissions.includes(
              PermissionNames.ManageOrganizations,
            );

            const hasManagePremiumServices = userInfo.permissions.includes(
              PermissionNames.ManagePremiumServices,
            );
            //user must have manage orgs permission to manage themes
            if (!hasManageOrgs && !hasManagePremiumServices) return of(false);

            //Org must have theming enabled
            const routeOrgId = this.getRouteOrgId();
            return this.organizationService.getOrganization(routeOrgId).pipe(
              map((org) => {
                return org.features.some(
                  (f) =>
                    f.featureName === FeatureNames.Theming && f.active === true,
                );
              }),
            );
          }
          case SecurityPolicies.RequiresManageTanks: {
            const hasManageTanks = userInfo.permissions.includes(
              PermissionNames.ManageTanks,
            );
            return of(hasManageTanks);
          }
          case SecurityPolicies.RequireSuperUserOrManageOrgs: {
            return of(
              userInfo?.permissions?.includes(
                PermissionNames.ManageOrganizations,
              ) || userInfo?.roles?.includes(RoleNames.SuperUser),
            );
          }
          case SecurityPolicies.RequiresManageLocations: {
            const hasManageLocations = userInfo.permissions.includes(
              PermissionNames.ManageLocations,
            );
            return of(hasManageLocations);
          }
          case SecurityPolicies.RequiresManageCallSchedule: {
            return of(
              userInfo?.permissions.includes(
                PermissionNames.ManageCallSchedule,
              ),
            );
          }
          case SecurityPolicies.RequiresManageUsers: {
            return of(userInfo?.permissions.includes(PermissionNames.ManageUsers));
          }
          case SecurityPolicies.RequiresManageOrganizations: {
            const hasManageOrganizations = userInfo.permissions.includes(
              PermissionNames.ManageOrganizations,
            );
            return of(hasManageOrganizations);
          }
          case SecurityPolicies.CanMoveTank: {
            const canMoveTank =
              userInfo.permissions.includes(PermissionNames.ManageTanks) &&
              userInfo.permissions.includes(PermissionNames.ManageLocations);
            return of(canMoveTank);
          }
          case SecurityPolicies.RequiresManageProducts: {
            return of(
              userInfo?.permissions.includes(PermissionNames.ManageProducts),
            );
          }
          case SecurityPolicies.RequiresManageTankProfile: {
            return of(
              userInfo?.permissions.includes(
                PermissionNames.ManageTankProfile,
              )
            );
          }
          case SecurityPolicies.RequiresManageDiagnostics: {
            return of(
              userInfo?.permissions?.includes(PermissionNames.ManageDiagnostic),
            );
          }
        }
      }),
    );
  }

  private getRouteOrgId() {
    let routeOrgId = 0;
    let pathSegements = this.location.path().toLowerCase().split('/');

    let valueIX = pathSegements.findIndex((ps) => ps === 'organization') + 1;
    routeOrgId = Number.parseInt(pathSegements[valueIX]);
    return routeOrgId;
  }

  private loadUserProfile(): Observable<IUserInformation> {
    const cached = this._userBehaviorSubject?.getValue();
    if (cached && cached.userId > 0) {
      this.logger.info('loadUserProfile behavior subject served=================', cached);
      return of(cached);
    }

    return this.appStore.store.select(selectUserInformation).pipe(
      switchMap((userInfo) => {
        //if the state store already has the user information
        if (userInfo && userInfo.userId > 0) {
          this.logger.info(
            'loadUserProfile state store served=================',
            userInfo,
          );
          return of(userInfo);
        } else {
          this.logger.info(
            'loadUserProfile getting from end point===================================',
            userInfo,
          );

          //get the user's infor from the end point.
          return this.getUserFromServer();
        }
      }),
    );
  }

  private processUserLoaded(userInfo: IUserInformation) {
    this.setUserId(userInfo.userId);

    // Seeds a tab that has no intent yet - a fresh sign-in, or the first load after this was
    // introduced - from whatever organization the session actually came back scoped to. Deliberately
    // only when unset: overwriting here would let a profile read triggered by something else quietly
    // move a tab that had already chosen, and the server has already had the final say on what this
    // session is scoped to by the time this runs.
    if (this.tabOrganization.get() === null) {
      this.tabOrganization.set(userInfo?.lastSelectedOrganizationId);
    }

    if (this._userBehaviorSubject) {
      if (this._userBehaviorSubject.getValue() !== userInfo) {
        this._userBehaviorSubject.next(userInfo);
      }
    } else {
      this._userBehaviorSubject = new BehaviorSubject<IUserInformation>(userInfo);
    }
  }

  @CacheBuster({
    isInstant: true,
  })
  private getUserFromServer(): Observable<IUserInformation> {
    globalCacheBusterNotifier.next();
    return this.restClient.getDirect<IUserInformation>(api`UserProfile`).pipe(
      tap((userInfo) => {
        if (!userInfo || userInfo.userId === 0) {
          this.navigateToLogout();
        }
        if (userInfo.mustChangePassword) {
          // Cache before redirecting: the navigation cancels the guard that is subscribed here, so
          // without this the redirected navigation finds no profile, fetches again, and loops.
          this.processUserLoaded(userInfo);
          this.navigateToMyAccount();
        }
      }),
    );
  }

  navigateToLogout() {
    this.router.navigate(['logout']);
  }

  navigateToMyAccount() {
    this.router.navigate(['my-account']);
  }
  private heartbeatUrl = api`Authenticate/heartbeat`;
  /**
   * Asserts the auth cookie and nothing else, so its 401 can only have come from the cookie scheme
   * itself - the one request in the app where a 401 means the session is over. Hence the opt-in.
   */
  public heartbeat(): Observable<IHeartbeatResult> {
    return this.restClient.getDirect<IHeartbeatResult>(
      this.heartbeatUrl,
      new HttpContext().set(EXPIRE_SESSION_ON_401, true),
    );
  }
}
